"use client"

import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import { supabaseClient } from "@/lib/supabase/client"

interface PhoneAuthContextType {
  isAuthenticated: boolean
  guestData: any | null
  authenticatePhone: (phone: string) => Promise<{ success: boolean; message: string; guest?: any }>
  authenticateWithGuest: (guest: any) => void
  logout: () => void
}

const PhoneAuthContext = createContext<PhoneAuthContextType | undefined>(undefined)

export function PhoneAuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [guestData, setGuestData] = useState<any | null>(null)

  // Verificar se já está autenticado ao carregar
  useEffect(() => {
    const checkAuth = () => {
      if (typeof window !== "undefined") {
        const phone = sessionStorage.getItem("guestPhone")
        const guest = sessionStorage.getItem("guestData")

        if (phone && guest) {
          try {
            const parsedGuest = JSON.parse(guest)
            console.log("Restaurando autenticação do sessionStorage:", parsedGuest.nome)
            setIsAuthenticated(true)
            setGuestData(parsedGuest)
          } catch (error) {
            console.log("Erro ao restaurar autenticação, limpando dados:", error)
            // Se houver erro ao fazer parse, limpar dados inválidos
            sessionStorage.removeItem("guestPhone")
            sessionStorage.removeItem("guestData")
          }
        }
      }
    }

    checkAuth()
  }, [])

  const authenticatePhone = async (phone: string) => {
    try {
      console.log("Iniciando autenticação para telefone:", phone)

      // Formatar telefone (remover caracteres especiais)
      const formattedPhone = phone.replace(/\D/g, "")
      console.log("Telefone formatado:", formattedPhone)

      // Verificar se o telefone existe na lista de convidados
      // Primeiro tenta buscar com o número completo
      let { data: guest, error } = await supabaseClient
        .from("convidados")
        .select("*")
        .eq("telefone", formattedPhone)
        .single()

      console.log("Primeira busca - guest:", guest?.nome, "error:", error?.message)

      // Se não encontrar e o número tem mais de 11 dígitos, pode ser internacional
      // Tenta buscar apenas com os últimos dígitos (número local)
      if (error && formattedPhone.length > 11) {
        const localPhone = formattedPhone.slice(-11) // Pega os últimos 11 dígitos
        console.log("Tentando busca com número local:", localPhone)

        const { data: guestLocal, error: errorLocal } = await supabaseClient
          .from("convidados")
          .select("*")
          .eq("telefone", localPhone)
          .single()

        console.log("Segunda busca - guest:", guestLocal?.nome, "error:", errorLocal?.message)

        if (!errorLocal && guestLocal) {
          guest = guestLocal
          error = null
        }
      }

      if (error || !guest) {
        console.log("Convidado não encontrado")
        return {
          success: false,
          message: "Telefone não encontrado na lista de convidados. Por favor, verifique o número informado.",
        }
      }

      console.log("Convidado encontrado:", guest.nome)

      // Salvar dados de autenticação
      setIsAuthenticated(true)
      setGuestData(guest)

      if (typeof window !== "undefined") {
        sessionStorage.setItem("guestPhone", formattedPhone)
        sessionStorage.setItem("guestData", JSON.stringify(guest))
        console.log("Dados salvos no sessionStorage")
      }

      return {
        success: true,
        message: "Autenticação realizada com sucesso!",
        guest,
      }
    } catch (error) {
      console.error("Erro na autenticação:", error)
      return {
        success: false,
        message: "Erro ao processar a autenticação. Tente novamente.",
      }
    }
  }

  const logout = () => {
    setIsAuthenticated(false)
    setGuestData(null)

    if (typeof window !== "undefined") {
      sessionStorage.removeItem("guestPhone")
      sessionStorage.removeItem("guestData")
    }
  }

  // Função para autenticar diretamente com dados do guest (sem refazer busca)
  const authenticateWithGuest = (guest: any) => {
    if (!guest) return

    setIsAuthenticated(true)
    setGuestData(guest)

    if (typeof window !== "undefined") {
      sessionStorage.setItem("guestPhone", guest.telefone || "")
      sessionStorage.setItem("guestData", JSON.stringify(guest))
    }
  }

  return (
    <PhoneAuthContext.Provider value={{ isAuthenticated, guestData, authenticatePhone, authenticateWithGuest, logout }}>
      {children}
    </PhoneAuthContext.Provider>
  )
}

export function usePhoneAuth() {
  const context = useContext(PhoneAuthContext)
  if (context === undefined) {
    throw new Error("usePhoneAuth deve ser usado dentro de um PhoneAuthProvider")
  }
  return context
}
